/**
 * Shared UI utilities - common functions used across components
 */

// ============== HTML Utilities ==============

/** Escape HTML to prevent XSS */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/** Safely get an element by ID, throws if not found */
export function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id) as T | null;
  if (!element) {
    throw new Error(`Element with id "${id}" not found`);
  }
  return element;
}

/** Get element by ID, returns null if not found */
export function getElement<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

// ============== Date/Time Formatting ==============

/** Format a date as relative time (e.g., "5 min ago", "2 hours ago") */
export function formatTimeAgo(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  
  return d.toLocaleDateString();
}

/** Format a date as short date string (e.g., "Feb 4, 2026") */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Format a timestamp as time string (e.g., "2:30 PM") */
export function formatTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ============== Avatar Rendering ==============

/** Generate avatar HTML with placeholder fallback */
export function renderAvatar(
  avatarUrl: string | null | undefined,
  username: string,
  displayName?: string | null,
  size: 'sm' | 'md' | 'lg' = 'md'
): string {
  const sizeClass = size === 'sm' ? 'avatar-sm' : size === 'lg' ? 'avatar-lg' : '';
  const initial = (displayName || username || '?').charAt(0).toUpperCase();
  
  if (avatarUrl) {
    return `<img src="${escapeHtml(avatarUrl)}" alt="${escapeHtml(username)}" class="avatar ${sizeClass}">`;
  }
  
  return `<div class="avatar avatar-placeholder ${sizeClass}">${initial}</div>`;
}

// ============== Loading/Empty States ==============

/** Render a loading spinner */
export function renderLoading(message = 'Loading...'): string {
  return `
    <div class="loading-state">
      <div class="spinner"></div>
      <p>${escapeHtml(message)}</p>
    </div>
  `;
}

/** Render an empty state message */
export function renderEmpty(message: string, hint?: string): string {
  return `
    <div class="empty-state">
      <p>${escapeHtml(message)}</p>
      ${hint ? `<p class="hint">${escapeHtml(hint)}</p>` : ''}
    </div>
  `;
}

/** Render an error state */
export function renderError(message: string): string {
  return `
    <div class="error-state">
      <p>${escapeHtml(message)}</p>
    </div>
  `;
}

// ============== Username/Profile Utilities ==============

/** Get display username with fallback */
export function getDisplayUsername(
  profile: { username?: string | null; display_name?: string | null } | null,
  userId: string
): string {
  return profile?.display_name || profile?.username || `user_${userId.substring(0, 8)}`;
}

/** Get display name (prefers display_name over username) */
export function getDisplayName(
  profile: { username?: string | null; display_name?: string | null } | null,
  fallback = 'Unknown'
): string {
  return profile?.display_name || profile?.username || fallback;
}

// ============== Event Handling ==============

/** Attach Enter key handler to an input */
export function onEnterKey(
  input: HTMLInputElement | HTMLTextAreaElement,
  callback: () => void
): void {
  input.addEventListener('keydown', (event) => {
    const e = event as KeyboardEvent;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      callback();
    }
  });
}

/** Set button disabled state with optional loading text */
export function setButtonLoading(
  button: HTMLButtonElement,
  loading: boolean,
  loadingText?: string
): void {
  button.disabled = loading;
  if (loadingText && loading) {
    button.dataset.originalText = button.textContent || '';
    button.textContent = loadingText;
  } else if (button.dataset.originalText) {
    button.textContent = button.dataset.originalText;
    delete button.dataset.originalText;
  }
}

// ============== Pagination ==============

/** Update pagination button states */
export function updatePaginationButtons(
  prevBtn: HTMLButtonElement | null,
  nextBtn: HTMLButtonElement | null,
  pageInfo: HTMLElement | null,
  currentPage: number,
  pageSize: number,
  totalItems: number
): void {
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  
  if (prevBtn) prevBtn.disabled = currentPage <= 1;
  if (nextBtn) nextBtn.disabled = currentPage >= totalPages;
  if (pageInfo) pageInfo.textContent = `Page ${currentPage} of ${totalPages}`;
}

// ============== Toast Notifications ==============

/** Show a toast notification */
export function showToast(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
  // Remove existing toast
  document.querySelector('.toast')?.remove();
  
  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);
  
  // Trigger animation
  requestAnimationFrame(() => toast.classList.add('show'));
  
  // Remove after delay
  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}
